import {
  getFormattedDate,
  isSomething,
  formatDateMDY,
} from "../../utils/utils";
import { ALERT_MESSAGES } from "./planner.constants";
import { recalculateTotal } from "./planner.utils";

// --------------------------------------LOGIC TO UPDATE MILESTONES--------------------------------------
export const milestoneUpdateLogic = ({ index, field, value, milestones }) => {
  const updatedMilestones = [...milestones];

  // Allow manual budget entry for fixed-fee types
  if (field === "budget") {
    updatedMilestones[index].budget = parseFloat(value) || 0;
    return updatedMilestones;
  }

  // Allow manual date range entry for fixed-fee types
  if (field === "dateRange") {
    updatedMilestones[index].dateRange = value;
    return updatedMilestones;
  }

  updatedMilestones[index][field] = value;
  if (field === "tasks") {
    const updatedTasks = value.map((task) => {
      const utcStartDate = task.startDate
        ? new Date(task.startDate).toISOString()
        : null;
      const utcEndDate = task.endDate
        ? new Date(task.endDate).toISOString()
        : null;

      return {
        ...task,
        startDate: utcStartDate,
        endDate: utcEndDate,
      };
    });
    updatedMilestones[index].tasks = updatedTasks;
    if (
      isSomething(updatedTasks[0].startDate) &&
      isSomething(updatedTasks[updatedTasks.length - 1].endDate)
    ) {
      const formattedStartDate = getFormattedDate(
        new Date(updatedTasks[0].startDate)
      );

      const formattedEndDate = getFormattedDate(
        new Date(updatedTasks[updatedTasks.length - 1].endDate)
      );

      updatedMilestones[
        index
      ].dateRange = `${formattedStartDate} - ${formattedEndDate}`;
    }
  }
  return updatedMilestones;
};

//---------------------------------------LOGIC TO HANDLE TASK UPDATES --------------------------------------
export const taskUpdateLogic = ({
  milestoneIndex,
  taskIndex,
  field,
  value,
  milestones,
}) => {
  const updatedMilestones = [...milestones];
  const task = updatedMilestones[milestoneIndex].tasks[taskIndex];

  if (field === "organization" && task.organization === "FKA") {
    if (task.hours > 0 || task.budget > 0) {
      if (value === "Client") {
        alert(ALERT_MESSAGES.Client);
        return; // Prevent the change
      }
    }
  }
  task[field] = value;

  if (["startDate", "endDate"].includes(field)) {
    task[field] = value ? new Date(value).toISOString() : ""; // Convert to UTC

    const tasks = updatedMilestones[milestoneIndex].tasks;
    const startDates = tasks
      .map((task) => task.startDate)
      ?.filter((x) => isSomething(x))
      ?.map((y) => new Date(y));

    const endDates = tasks
      .map((task) => task.endDate)
      ?.filter((x) => isSomething(x))
      ?.map((y) => new Date(y));

    const minStartDate = new Date(Math.min(...startDates));
    const maxEndDate = new Date(Math.max(...endDates));

    const noDates = !isSomething(startDates) || !isSomething(endDates);

    if (noDates) {
      updatedMilestones[milestoneIndex].dateRange =
        ALERT_MESSAGES.IncorrectDates;
    }

    if (!noDates) {
      const formatedStartDate = getFormattedDate(minStartDate);
      const formattedEndDate = getFormattedDate(maxEndDate);
      const stringValue = `${formatedStartDate} - ${formattedEndDate}`;
      updatedMilestones[milestoneIndex].dateRange = stringValue;
    }
  }

  if (field === "budget" || field === "hours")
    task[field] = parseFloat(value).toFixed(2);

  if (field === "endDate") {
    const startDate = new Date(task.startDate);
    const endDate = new Date(value);

    if (startDate && endDate && startDate > endDate) {
      alert("End date cannot be earlier than start date.");
      return;
    }
  }

  return updatedMilestones;
};

//---------------------------------------LOGIC TO DELETE TASK --------------------------------------
export const deleteTaskLogic = ({ milestoneIndex, taskIndex, milestones }) => {
  const milestone = milestones[milestoneIndex];
  if (milestone.tasks.length === 1) {
    alert(ALERT_MESSAGES.deleteTask);
    return milestones;
  }

  const updatedMilestones = [...milestones];
  updatedMilestones[milestoneIndex].tasks.splice(taskIndex, 1);

  // Calculate the date range again after deleting the task
  const tasks = updatedMilestones[milestoneIndex].tasks;
  const startDates = tasks
    .map((task) => task.startDate && new Date(task.startDate))
    .filter((d) => d instanceof Date && !isNaN(d));

  const endDates = tasks
    .map((task) => task.endDate && new Date(task.endDate))
    .filter((d) => d instanceof Date && !isNaN(d));

  if (startDates.length === 0 || endDates.length === 0) {
    updatedMilestones[milestoneIndex].dateRange = "";
    return updatedMilestones;
  }

  const earliestStartDate = new Date(Math.min(...startDates));
  const latestEndDate = new Date(Math.max(...endDates));

  const formattedStartDate = getFormattedDate(earliestStartDate);
  const formattedEndDate = getFormattedDate(latestEndDate);

  updatedMilestones[
    milestoneIndex
  ].dateRange = `${formattedStartDate} - ${formattedEndDate}`;

  return updatedMilestones;
};

//---------------------------------------LOGIC TO UPDATE MATERIAL --------------------------------------

const handleMaterialChange = ({ materialIndex, field, value, materials }) => {
  const updatedMaterials = [...materials];
  const mat = updatedMaterials[materialIndex];

  // Simply update the field on the material object
  mat[field] = value;

  // If the field affects the total, recalc the total value
  if (field === "quantity" || field === "cost" || field === "markup") {
    const quantity = parseFloat(mat.quantity) || 0;
    const cost = parseFloat(mat.cost) || 0;
    const markup = parseFloat(mat.markup) || 0;
    const total = quantity * cost * (1 + markup / 100);
    mat.total = total.toFixed(2);
  }

  return updatedMaterials;
};

export const updateMaterialLogic = ({
  materialIndex,
  taskIndex, // can be ignored
  field,
  value,
  materials,
}) => {
  const updatedMaterials = [...materials];
  const task = updatedMaterials[materialIndex]; // no `.material`

  if (!task) {
    console.error("Material not found at index", materialIndex);
    return updatedMaterials;
  }

  if (field === "markup") {
    const markupRegex = /^[0-9]{0,3}$/;
    if (markupRegex.test(value)) task[field] = value;
  } else if (field === "override") {
    if (value.trim() === "") {
      task[field] = "";
      recalculateTotal(task);
    } else {
      task[field] = parseFloat(value);
      task.total = task[field];
    }
  } else {
    task[field] = value;
  }

  if (
    !task.override &&
    (field === "quantity" || field === "cost" || field === "markup")
  ) {
    recalculateTotal(task);
  }

  return updatedMaterials;
};

//---------------------------------------LOGIC TO NAVIGATE TO BUDGET BUILDER --------------------------------------

export const navigateToBudgetLogic = ({ milestone, navigate, id }) => {
  if (!milestone) {
    return;
  }

  // Check if the milestone has a valid name and valid tasks
  const isValidName = milestone.name && milestone.name.trim() !== "";

  const hasValidTasks =
    milestone.tasks &&
    milestone.tasks.some(
      (task) => task.description && task.description.trim() !== ""
    );

  if (isValidName && hasValidTasks) {
    navigate(`/projectsview/${id}/budget/`);
  } else {
    alert(ALERT_MESSAGES.AddBudgetDescription);
  }
};

// ---------------------------------------LOGIC TO HANDLE DATES IN CASE OF TEMPLATE SELECTION --------------------------------------
export const dateAdustmentForTemplateSelectionLogic = (
  templateMilestones,
  newStartDate,
  plannerType,
  origStartStr,
  origEndStr
) => {
  // Normalize new start to midnight UTC
  const newStart = new Date(
    new Date(newStartDate).toISOString().split("T")[0] + "T00:00:00Z"
  );

  // Helper to compute original range for various types
  let originalStart, originalEnd;
  if (plannerType === "Fixed Fee Project") {
    // Use top-level dates
    originalStart = origStartStr ? new Date(origStartStr) : newStart;
    originalEnd = origEndStr ? new Date(origEndStr) : newStart;
  } else if (
    plannerType === "Time + Materials" &&
    templateMilestones[0]?.tasks?.length > 0
  ) {
    // Flatten all task dates
    const starts = templateMilestones.flatMap((m) =>
      m.tasks.map((t) => new Date(t.startDate))
    );
    const ends = templateMilestones.flatMap((m) =>
      m.tasks.map((t) => new Date(t.endDate))
    );
    originalStart = new Date(Math.min(...starts));
    originalEnd = new Date(Math.max(...ends));
  } else if (
    plannerType === "Fixed Fee Deliverable" &&
    templateMilestones[0]?.dateRange
  ) {
    // Flatten all deliverable ranges
    const ranges = templateMilestones.map((m) => m.dateRange.split(" - "));
    const starts = ranges.map(([s]) => new Date(s));
    const ends = ranges.map(([, e]) => new Date(e));
    originalStart = new Date(Math.min(...starts));
    originalEnd = new Date(Math.max(...ends));
  } else {
    // No valid dates => default to newStart
    originalStart = newStart;
    originalEnd = newStart;
  }

  // Calculate shift
  const diff = newStart.getTime() - originalStart.getTime();

  // Compute adjusted project-level dates
  const adjustedStart = new Date(newStart.getTime());
  const adjustedEnd = new Date(originalEnd.getTime() + diff);
  const plannerStartDate = formatDateMDY(adjustedStart);
  const plannerEndDate = formatDateMDY(adjustedEnd);

  // Adjust deliverable milestones
  const adjustedMilestones = templateMilestones.map((m) => {
    // Time + Materials: shift tasks and recalc dateRange
    if (plannerType === "Time + Materials" && m.tasks?.length > 0) {
      const adjTasks = m.tasks.map((t) => {
        const s = new Date(t.startDate);
        const e = new Date(t.endDate);
        return {
          ...t,
          startDate: new Date(s.getTime() + diff).toISOString(),
          endDate: new Date(e.getTime() + diff).toISOString(),
        };
      });
      const dates = adjTasks.flatMap((t) => [
        new Date(t.startDate),
        new Date(t.endDate),
      ]);
      const min = new Date(Math.min(...dates));
      const max = new Date(Math.max(...dates));
      return {
        ...m,
        tasks: adjTasks,
        dateRange: `${getFormattedDate(min)} - ${getFormattedDate(max)}`,
      };
    }
    // Fixed Fee Deliverable: shift only dateRange
    if (plannerType === "Fixed Fee Deliverable" && m.dateRange) {
      const [sStr, eStr] = m.dateRange.split(" - ");
      const s = new Date(sStr);
      const e = new Date(eStr);
      return {
        ...m,
        dateRange: `${formatDateMDY(
          new Date(s.getTime() + diff)
        )} - ${formatDateMDY(new Date(e.getTime() + diff))}`,
      };
    }
    // Other types: leave deliverable unchanged
    return m;
  });

  return { milestones: adjustedMilestones, plannerStartDate, plannerEndDate };
};
